import moment from "moment";

export const SYSADMIN_ROOT = "/sysadmin";

export type SysadminSection = "companies" | "offices" | "eaterGroups" | "users";

export const sysadminPaths = {
    list: (section: SysadminSection) => `${SYSADMIN_ROOT}/${section}`,
    /** The users list with a user opened in the side panel. */
    userPanel: (email: string, params?: Record<string, string>) =>
        `${SYSADMIN_ROOT}/users?${new URLSearchParams({ ...params, user: email }).toString()}`,
    detail: (section: SysadminSection, id: string) => `${SYSADMIN_ROOT}/${section}/${encodeURIComponent(id)}`,
    create: (section: SysadminSection, params?: Record<string, string>) => {
        const query = params ? new URLSearchParams(params).toString() : "";
        return `${SYSADMIN_ROOT}/${section}/new${query ? "?" + query : ""}`;
    },
    versions: (section: SysadminSection, id: string) => `${SYSADMIN_ROOT}/${section}/${encodeURIComponent(id)}/versions`,
    version: (section: SysadminSection, id: string, version: string) =>
        `${SYSADMIN_ROOT}/${section}/${encodeURIComponent(id)}/versions/${encodeURIComponent(version)}`,
};

/** Parses service timestamps, which arrive as "YYYYMMDDHHmmss" strings (ISO is accepted too). */
export function parseServiceDate(date: Date | string | undefined | null): Date | undefined {
    if (!date) return undefined;
    const m = typeof date === "string" && /^\d{14}$/.test(date) ? moment(date, "YYYYMMDDHHmmss") : moment(date);
    return m.isValid() ? m.toDate() : undefined;
}

export function formatDateTime(date: Date | string | undefined | null): string {
    const parsed = parseServiceDate(date);
    return parsed ? moment(parsed).format("DD-MM-YYYY HH:mm") : "";
}

/** Extracts a human readable message from an axios/datasolvr error. */
export function errorMessage(e: any, fallback = "An error occurred"): string {
    const data = e?.response?.data;
    if (data?.content && Array.isArray(data.content) && data.content.length > 0) {
        return data.content[data.content.length - 1].message ?? fallback;
    }
    if (typeof data === "string" && data.length > 0) {
        return data;
    }
    if (e?.response?.status === 0 || (e?.request && !e?.response)) {
        return "Unable to reach server";
    }
    return e?.message ?? fallback;
}

/** Route params are not decoded by react-router (e.g. emails arrive as "a%40b.dk"). */
export function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}
